import React from 'react';
import * as Sentry from '@sentry/react';

// ─── ERROR BOUNDARY ───────────────────────────────────────────────────────────
//
//  Catches any unhandled render errors in the React tree and shows a themed
//  recovery screen instead of a blank white page.
//
//  Uses Sentry.ErrorBoundary to automatically report component crashes.
//  Must be a class component — React error boundaries cannot be written as
//  functional components.
//
//  Loaded before App.jsx and wraps the ReactDOM.createRoot render call.
//

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    console.error('[Chronos] Uncaught render error:', error);
    console.error(info.componentStack);
    Sentry.captureException(error, { extra: { componentStack: info.componentStack } });
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    const message = this.state.error?.message || 'An unexpected error occurred.';

    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--bg)',
        padding: 24,
        fontFamily: 'var(--font-body)',
      }}>
        <div style={{ width: '100%', maxWidth: 480, textAlign: 'center' }}>

          {/* Icon */}
          <div style={{ fontSize: 52, marginBottom: 20, lineHeight: 1 }} aria-hidden="true">⚠️</div>

          {/* Heading */}
          <h1 style={{
            fontFamily: 'var(--font-display)',
            fontSize: 28,
            color: 'var(--accent)',
            marginBottom: 12,
            letterSpacing: '-0.02em',
          }}>
            Something went wrong
          </h1>

          {/* Body */}
          <p style={{ color: 'var(--text2)', fontSize: 15, lineHeight: 1.6, marginBottom: 8 }}>
            Chronos ran into an unexpected error and couldn't continue.
          </p>
          <p style={{ color: 'var(--text3)', fontSize: 13, lineHeight: 1.5, marginBottom: 32 }}>
            Your data is safe. Reloading the page will take you back to where you were.
          </p>

          {/* Error detail (collapsed, for debugging) */}
          <details style={{ marginBottom: 28, textAlign: 'left' }}>
            <summary style={{
              fontSize: 12,
              color: 'var(--text3)',
              cursor: 'pointer',
              userSelect: 'none',
              marginBottom: 8,
            }}>
              Error details
            </summary>
            <pre style={{
              background: 'var(--bg3)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius)',
              padding: '10px 14px',
              fontSize: 11,
              color: 'var(--danger)',
              overflowX: 'auto',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}>
              {message}
            </pre>
          </details>

          {/* Reload button */}
          <button
            className="btn-primary"
            onClick={() => { window.location.href = '/'; }}
            style={{ minWidth: 160 }}
          >
            Reload Chronos
          </button>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
